import React, { useState, useRef, useEffect, MutableRefObject } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';
import { 
    Info,
} from 'lucide-react';

import { ReactComponent as EditIcon } from '@ICONS/edit-icon-2.svg';

import {
    AdminSingleSectionMeta,
    AdminSingleSectionDataVariant,
    AdminSingleSectionData,
} from '@INTERFACES/admin-api/sections';

import { isContentEmptyCheck } from '@HELPERS/isContentEmptyCheck';

import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import useEditVariantModal from '@HOOKS/store/modals/useEditVariantModal';
import useDeleteVariantModal from '@HOOKS/store/modals/useDeleteVariantModal';
import RoundIconButton from '@COMPONENTS/COMMON/buttons/RoundIconButton';

import VariantEditor from '@PAGES/admin/edit-section/components/editors/VariantEditor';
import VariantBodySidebar from '@PAGES/admin/edit-section/components/VariantBodySidebar';
import VariantMetadata from '@PAGES/admin/edit-section/components/VariantMetadata';

import styles from './Variant.module.scss';

const cx: CX = classnames.bind(styles);

function Variant(props: Props) {
    const {
        variant,
        sectionData,
        sectionMeta,
        handleToggleDefault,
    } = props;

    const editorRef: MutableRefObject<string> = useRef('');
    const originalContentRef: MutableRefObject<string> = useRef('');
    const isInitializedRef: MutableRefObject<boolean> = useRef(false);
    const [contentIsEmpty, setContentIsEmpty] = useState<boolean>(true);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [selectedVersion, setSelectedVersion] = useState<any>(undefined);
    const [showDefaultVariantHelp, setShowDefaultVariantHelp] = useState<boolean>(false);

    const { canEditDocument } = useDocumentPermissions({
        documentOwnerId: sectionData.document.owner,
        permissions: sectionMeta.permissions,
    });

    const { actions: editVariantModalActions } = useEditVariantModal();
    const { actions: deleteVariantModalActions } = useDeleteVariantModal();

    // Track original content when variant or selected version changes
    useEffect(() => {
        let originalContent = '';
        
        if (selectedVersion && selectedVersion.value !== 0) {
            // If a specific version is selected, use that version's body
            const version = variant.versions.find(v => v.id === selectedVersion.value);
            originalContent = version?.body || '';
        } else {
            // If draft (value 0) or no version selected, use current variant body
            originalContent = variant.body || '';
        }
        

        originalContentRef.current = originalContent;
        isInitializedRef.current = false; // Reset initialization flag
        
        // Reset changes state when switching versions/variants
        setHasChanges(false);
    }, [variant.body, variant.versions, selectedVersion]);

    function getEditorContent() {
        return editorRef.current;
    }

    function checkForChanges(currentContent: string) {
        editorRef.current = currentContent;
        
        // Normalize content for comparison (remove extra whitespace, consistent formatting)
        const normalizeContent = (content: string) => content
            .replace(/\s+/g, ' ') // Replace multiple whitespaces with single space
            .replace(/>\s+</g, '><') // Remove spaces between tags
            .trim();
        
        const normalizedCurrent = normalizeContent(currentContent);
        const normalizedOriginal = normalizeContent(originalContentRef.current);
        
        // If this is the first call after setting original content (editor initialization),
        // and the content matches the original, don't mark as changed
        if (!isInitializedRef.current) {
            isInitializedRef.current = true;
            

            
            if (normalizedCurrent === normalizedOriginal) {
                setHasChanges(false);
                return;
            }
        }
        
        const contentChanged = normalizedCurrent !== normalizedOriginal;
        

        
        setHasChanges(contentChanged);
    }

    const handleEditVariant = () => {
        editVariantModalActions.open({
            documentId: sectionData.document.id,
            chapterId: sectionData.chapter.id,
            sectionId: sectionData.id,
            variantId: variant.id,
            canEditDocument,
        });
    };

    const handleDeleteVariant = () => {
        deleteVariantModalActions.open({
            id: variant.id,
            documentId: sectionData.document.id,
            chapterId: sectionData.chapter.id,
            sectionId: sectionData.id,
            variant,
        });
    };

    const handleToggleDefaultStatus = () => {
        // Pass the current editor content to ensure it's preserved
        const currentContent = getEditorContent();
        handleToggleDefault(variant.id, currentContent);
    };

    return (
        <div
            data-variant={variant.id}
            className={cx('variant')}
        >
            <div
                id={`variant-${variant.id}`}
                className={cx('anchor')}
            />

            {/* Variant Card */}
            <div className={cx('variant-card')}>
                {/* Header Section */}
                <div className={cx('variant-header', { 'default-variant': variant.isDefault })}>
                    <div className={cx('header-content')}>
                        <div className={cx('variant-info')}>
                            <div className={cx('title-and-status-row')}>
                                <h3 className={cx('variant-title')}>
                                    {variant.isDefault
                                        ? 'Default Section Variant'
                                        : variant.name || `Variant ${variant.id}`}
                                    <span
                                        className={cx('status-badge', {
                                            published: variant.isPublished,
                                            draft: !variant.isPublished,
                                        })}
                                    >
                                        {variant.isPublished ? 'Published' : 'Draft'}
                                    </span>
                                    {variant.isDefault && (
                                        <button
                                            type="button"
                                            className={cx('info-icon-button')}
                                            onClick={() => setShowDefaultVariantHelp(!showDefaultVariantHelp)}
                                            title="Learn more about default variants"
                                            aria-label="Learn more about default variants"
                                        >
                                            <Info size={16} />
                                        </button>
                                    )}
                                </h3>
                            </div>
                        </div>

                        <div className={cx('variant-actions')}>
                            {canEditDocument && (
                                <>
                                    {/* Edit Variant Button (only for non-default variants) */}
                                    {!variant.isDefault && (
                                        <button
                                            type="button"
                                            className={cx('edit-variant-button')}
                                            onClick={handleEditVariant}
                                            style={{ marginRight: '12px' }}
                                            title="Edit Name & Applicability Settings"
                                        >
                                            <EditIcon width={16} height={16} />
                                            <span>Edit Name & Applicability Settings</span>
                                        </button>
                                    )}

                                    {/* Delete Variant Button (only for non-default variants) */}
                                    {!variant.isDefault && (
                                        <div className={cx('delete-variant-button')}>
                                            <RoundIconButton
                                                icon="delete"
                                                onClick={handleDeleteVariant}
                                                tooltipTitle="Delete Variant"
                                                locked={!canEditDocument}
                                            />
                                        </div>
                                    )}

                                    {/* Default Toggle */}
                                    <div className={cx('default-toggle-wrapper')}>
                                        <label className={cx('default-toggle', { disabled: variant.isDisable })}>
                                            <input
                                                type="checkbox"
                                                checked={variant.isDefault}
                                                disabled={variant.isDisable}
                                                onChange={handleToggleDefaultStatus}
                                                className={cx('toggle-input')}
                                            />
                                            <span className={cx('toggle-slider')} />
                                            <span className={cx('toggle-label')}>
                                                <span className={cx('toggle-text')}>Default Section Variant</span>
                                                <button
                                                    type="button"
                                                    className={cx('toggle-info-icon-button')}
                                                    onClick={() => setShowDefaultVariantHelp(!showDefaultVariantHelp)}
                                                    title="Learn more about default variants"
                                                    aria-label="Learn more about default variants"
                                                    disabled={variant.isDisable}
                                                >
                                                    <Info size={16} />
                                                </button>
                                            </span>
                                        </label>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Default Variant Help Section */}
                    {showDefaultVariantHelp && (
                        <div className={cx('default-variant-help')}>
                            <div className={cx('help-header')}>
                                <div className={cx('help-icon')}>
                                    <Info size={16} />
                                </div>
                                <div className={cx('help-title')}>What is a Default Section Variant?</div>
                            </div>
                            <div className={cx('help-content')}>
                                <div className={cx('help-description')}>
                                    Default section variants serve as fallback content when no specific variant matches the document generation criteria.
                                </div>
                                <ul className={cx('help-features')}>
                                    <li className={cx('help-feature')}>
                                        <div className={cx('feature-bullet')} />
                                        <span>Contains generic content that applies to most scenarios</span>
                                    </li>
                                    <li className={cx('help-feature')}>
                                        <div className={cx('feature-bullet')} />
                                        <span>Used by the system when no better match is found during PDF generation</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}

                </div>

                {/* Content Section */}
                <div className={cx('variant-content')}>
                    <div className={cx('editor-section')}>
                        <div className={cx('editor-wrapper')}>
                            <VariantEditor
                                variant={variant}
                                selectedVersion={selectedVersion}
                                sectionData={sectionData}
                                onEditorChange={(content: string) => {
                                    setContentIsEmpty(isContentEmptyCheck(content));
                                    checkForChanges(content);
                                }}
                            />
                        </div>
                    </div>

                    <div className={cx('sidebar-section')}>
                        <VariantBodySidebar
                            contentIsEmpty={contentIsEmpty}
                            hasChanges={hasChanges}
                            variant={variant}
                            selectedVersion={selectedVersion}
                            sectionData={sectionData}
                            sectionMeta={sectionMeta}
                            getEditorContent={() => getEditorContent()}
                            onVersionChange={(val: any) => setSelectedVersion(val)}
                        />
                    </div>
                </div>

                {/* Metadata Section */}
                <div className={cx('metadata-section')}>
                    <VariantMetadata
                        variant={variant}
                        sectionData={sectionData}
                        sectionMeta={sectionMeta}
                    />
                </div>
            </div>
        </div>
    );
}

Variant.propTypes = {
    handleToggleDefault: PropTypes.func.isRequired,
};

type Props = InferProps<typeof Variant.propTypes> & {
    variant: AdminSingleSectionDataVariant & { isDisable?: boolean },
    sectionData: AdminSingleSectionData,
    sectionMeta: AdminSingleSectionMeta,
};

export default Variant;
